var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/components/session-timeout.component.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  HeadingText,
  Modal,
  Box,
  UtilityText
} from "@leagueplatform/genesis-core";
import { useGetIsImpersonator } from "@leagueplatform/auth-utils";
import { captureError } from "@leagueplatform/observability";
import { FormattedMessage } from "react-intl";
import throttle from "lodash/throttle";
var DEBOUNCE_MILLIS = 1e3;
var SESSION_TIMEOUT_BUFFER = 15e4;
var TimeoutModalContent = ({
  onLogout,
  onExtend
}) => {
  const isImpersonator = useGetIsImpersonator();
  let messageId = "SESSION_ABOUT_TO_TIMEOUT";
  if (isImpersonator) {
    messageId = "SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR";
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Modal.Title, null, /* @__PURE__ */ React.createElement(HeadingText, { css: { textAlign: "center" }, level: "1", size: "lg" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "SESSION_TIMEOUT" }))), /* @__PURE__ */ React.createElement(
    UtilityText,
    {
      css: {
        marginTop: "$oneAndHalf",
        marginBottom: "$oneAndHalf",
        display: "block"
      }
    },
    /* @__PURE__ */ React.createElement(FormattedMessage, { id: messageId })
  ), /* @__PURE__ */ React.createElement(Box, { css: { display: "flex", justifyContent: "end" } }, /* @__PURE__ */ React.createElement(Button, { priority: "secondary", onClick: onLogout }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "SESSION_LOG_OUT" })), !isImpersonator && /* @__PURE__ */ React.createElement(Button, { css: { marginInlineStart: "$one" }, onClick: onExtend }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "CONTINUE_SESSION" }))));
};
var SessionTimeout = ({
  timeoutInterval,
  getIsAuthenticated,
  onSessionExpire,
  onSessionExtend
}) => {
  const [showModal, setShowModal] = useState(false);
  const sessionTimeoutBroadcastChannel = useRef(
    void 0
  );
  const promptTimeout = useRef();
  const expiryTimeout = useRef();
  const clearTimeouts = useCallback(() => {
    window.clearTimeout(promptTimeout.current);
    window.clearTimeout(expiryTimeout.current);
  }, []);
  const restartTimer = useCallback(
    (browserContextSource) => {
      clearTimeouts();
      if (browserContextSource === "self") {
        if (sessionTimeoutBroadcastChannel.current) {
          sessionTimeoutBroadcastChannel.current.postMessage("");
        } else {
          captureError(
            new Error(
              "Error sending session timeout postMessage, broadcast channel is not created."
            ),
            {
              errorName: "SessionTimeout - BroadcastChannel Error"
            }
          );
        }
      }
      promptTimeout.current = window.setTimeout(() => __async(void 0, null, function* () {
        if (yield getIsAuthenticated()) {
          setShowModal(true);
        }
      }), timeoutInterval - SESSION_TIMEOUT_BUFFER);
      expiryTimeout.current = window.setTimeout(
        onSessionExpire,
        timeoutInterval
      );
    },
    [clearTimeouts, getIsAuthenticated, onSessionExpire, timeoutInterval]
  );
  useEffect(() => {
    sessionTimeoutBroadcastChannel.current = new BroadcastChannel(
      "league-session-timeout-timer-restart"
    );
    const onInteractionEvent = throttle(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      restartTimer("self");
    }, DEBOUNCE_MILLIS);
    document.addEventListener("visibilitychange", onInteractionEvent);
    document.addEventListener("click", onInteractionEvent);
    document.addEventListener("keydown", onInteractionEvent);
    sessionTimeoutBroadcastChannel.current.onmessage = () => {
      restartTimer("other");
    };
    restartTimer("self");
    return () => {
      clearTimeouts();
      document.removeEventListener("visibilitychange", onInteractionEvent);
      document.removeEventListener("click", onInteractionEvent);
      document.removeEventListener("keydown", onInteractionEvent);
      sessionTimeoutBroadcastChannel.current.close();
    };
  }, [clearTimeouts, restartTimer]);
  const logoutClickHandler = useCallback(() => {
    clearTimeouts();
    setShowModal(false);
    onSessionExpire();
  }, [clearTimeouts, onSessionExpire]);
  const extendSessionClickHandler = useCallback(() => {
    restartTimer("self");
    setShowModal(false);
    onSessionExtend();
  }, [onSessionExtend, restartTimer]);
  return /* @__PURE__ */ React.createElement(Modal.Root, { open: showModal }, /* @__PURE__ */ React.createElement(Modal.Content, null, /* @__PURE__ */ React.createElement(
    TimeoutModalContent,
    {
      onLogout: logoutClickHandler,
      onExtend: extendSessionClickHandler
    }
  )));
};
export {
  SessionTimeout
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/session-timeout'] = window.leagueSDKsPresent['@leagueplatform/session-timeout'] || [];
    window.leagueSDKsPresent['@leagueplatform/session-timeout'].push('2.23.5');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/session-timeout'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/session-timeout module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map